// Autobuses «en tiempo real» en el mapa. Normalmente los manda ya deducidos el servicio
// `server/` (por SSE en /api/live/stream): él consulta todas las paradas sin parar, así que al
// abrir la app los buses salen al momento y ya confirmados. Si el servicio no responde, la app
// consulta ella misma los minutos de las paradas de las líneas activas, como antes. En los dos
// casos el motor es el mismo (engine.ts) y aquí solo se mueven los marcadores entre datos y datos.

import type { Dataset, LineId } from "../data";
import { haversine } from "../geo";
import type { MapAdapter, StopLabelInput } from "../map/types";
import { LIGHT_LINES, STOP_ETA_LABELS } from "../config";
import { fetchEstimations, type ApiEstimation } from "./api";
import { nextStopAt } from "./infer";
import { pointAt } from "./geometry";
import {
  BLEND_MS,
  EVIDENCE_STALE_MS,
  EXTRAPOLATE_MS,
  createEngine,
  type BusState,
  type Tracked,
} from "./engine";
import { createShown, type ShownBus } from "./shown";

const POLL_MIN_S = 30;
const POLL_MAX_S = 60;
const POLL_S_PER_STOP = 0.2; // con muchas líneas activas espaciamos para no saturar el origen
const CONCURRENCY = 6;
// Una consulta que lleva más que esto no va a terminar bien (red caída, móvil dormido): se
// abandona y empieza otra.
const POLL_STUCK_MS = 60_000;
const FRAME_MS = 500;
const STALE_MS = 150_000; // sin datos frescos en 2,5 min quitamos los buses
const SERVER_WAIT_MS = 6_000; // al arrancar, lo que esperamos al servicio antes de consultar a mano
const SERVER_FRESH_MS = 90_000; // el servicio manda cada ~30 s: sin noticias en esto, a mano
const HANDOVER_M = 400; // al pasar de datos propios a los del servicio: mismo bus si está a menos
// Posiciones que manda el servicio cada segundo: sin noticias en esto, se calculan aquí.
const POS_FRESH_MS = 5_000;
const POS_BACK_M = 50; // retraso de red: menos que esto atrás no se retrocede, se espera
const LABEL_FRESH_MS = 150_000;

// Lo que manda el servicio en cada actualización.
export interface LiveMessage {
  now: number; // reloj del servidor
  observedAt?: number; // última consulta exitosa, aunque estuviese vacía
  buses: BusState[];
  etas: Record<string, [string, string, number, number?][]>; // parada → [línea, destino, minutos, instante de consulta]
  polls?: Record<string, [number, number]>; // parada → [última lectura, siguiente lectura (0 = ya)]
}

// Lo que manda el servicio cada segundo: dónde pintar cada bus (calculado allí, igual para todos).
// [id, línea, número, trayecto, metros, m/s, hasta, parada en la que late, lat, lng, rumbo,
// deslizándose al trayecto nuevo]
export type PosRow = [
  string,
  string,
  number | null,
  string,
  number,
  number,
  number,
  number | null,
  number,
  number,
  number,
  0 | 1,
];
export interface PosMessage {
  now: number;
  b: PosRow[];
}

export interface LiveTracker {
  setLines(ids: readonly LineId[]): void;
  setEnabled(on: boolean): void;
  describe(id: string): string | null;
  lineOf(id: string): LineId | null;
  // Minutos del próximo bus de `line` en la parada, en el sentido del trayecto `routeId`
  // (de la última consulta); null si no hay dato.
  nextAt(stopId: number, line: LineId, routeId: number): number | null;
  // Tramos (parada anterior → siguiente) donde hay un bus ahora mismo en ese trayecto, y la
  // parada si está en ella.
  busesOn(
    line: LineId,
    routeId: number,
  ): { fromStopId: number; toStopId: number; atStopId: number | null }[];
  // ¿Hay un bus de `line` parado en la parada ahora mismo?
  atStop(stopId: number, line: LineId): boolean;
  onUpdate(cb: () => void): () => void;
}

export interface LiveTrackerOptions {
  ds: Dataset;
  adapter: MapAdapter;
  onError?: (err: unknown) => void;
  // Sin servicio (tests, desarrollo): siempre consulta la app.
  server?: boolean;
}

export function createLiveTracker(o: LiveTrackerOptions): LiveTracker {
  const engine = createEngine(o.ds);
  const useServer = o.server ?? typeof EventSource !== "undefined";
  let enabled = false;
  let active = new Set<LineId>();
  let tracked: Tracked[] = [];
  let seq = 0;
  let generation = 0; // invalida consultas en vuelo al cambiar líneas o apagar
  let pollTimer: number | null = null;
  let frameTimer: number | null = null;
  let lastPoll = 0;
  let polling = false;
  let pollCtl: AbortController | null = null;
  let errorShown = false;
  let lastEtas = new Map<number, ApiEstimation[]>();
  let lastPolls = new Map<number, { at: number; due: number }>(); // reloj del móvil
  let numById = new Map<string, number>(); // número de cada bus en su línea (del servidor)
  // Líneas ya consultadas. La primera consulta de una línea lo enseña todo: no hay otra con la
  // que confirmar.
  let polledLines = new Set<LineId>();
  let source: EventSource | null = null;
  let serverAt = 0; // última noticia del servicio
  const updateCbs: (() => void)[] = [];

  const serverFresh = (): boolean => serverAt > 0 && Date.now() - serverAt < SERVER_FRESH_MS;

  // Sin servicio, dónde pintar cada bus se calcula aquí, con la misma regla que allí.
  const shown = createShown(engine);
  const atStopNow = new Map<string, number>(); // bus → parada en la que se pinta
  // Lo último que ha mandado el servicio y cuándo llegó (reloj del móvil).
  let pos = new Map<string, { row: PosRow; at: number }>();
  let posAt = 0;
  // Lo pintado de cada bus con datos del servicio: avanza entre mensajes y no retrocede.
  const drawn = new Map<string, { routeKey: string; d: number; ll: ShownBus["ll"] }>();

  const fromServer = (now: number): ShownBus[] => {
    const out: ShownBus[] = [];
    for (const [id, { row, at }] of pos) {
      const [, line, , routeKey, d, mps, cap, atStopId, lat, lon, bearing, blending] = row;
      if (!active.has(line)) continue;
      const geom = engine.entryOf(routeKey)?.geom;
      if (!geom) continue;
      const prev = drawn.get(id);
      let ll = { lat, lon };
      let shownD = d;
      let dir = bearing;
      if (blending) {
        // Media vuelta: se desliza en un segundo desde donde se veía hasta donde dice.
        const k = Math.min(1, (now - at) / 1000);
        if (prev)
          ll = {
            lat: prev.ll.lat + (lat - prev.ll.lat) * k,
            lon: prev.ll.lon + (lon - prev.ll.lon) * k,
          };
      } else {
        shownD = Math.min(cap, d + (mps * (now - at)) / 1000);
        if (prev && prev.routeKey === routeKey && shownD < prev.d && prev.d - shownD < POS_BACK_M)
          shownD = prev.d;
        const p = pointAt(geom, shownD);
        if (p) [ll, dir] = [p.ll, p.bearing];
      }
      drawn.set(id, { routeKey, d: shownD, ll });
      out.push({
        id,
        line,
        routeKey,
        d: shownD,
        mps,
        cap,
        ll,
        bearing: dir,
        blending: !!blending,
        atStopId,
      });
    }
    for (const id of drawn.keys()) if (!pos.has(id)) drawn.delete(id);
    return out;
  };

  const render = (): void => {
    const now = Date.now();
    if (tracked.length > 0 && now - lastPoll > STALE_MS) {
      tracked = [];
      polledLines.clear();
    }
    atStopNow.clear();
    const byId = new Map(tracked.map((t) => [t.id, t]));
    const buses =
      now - posAt < POS_FRESH_MS ? fromServer(now) : shown.update(tracked, lastEtas, active, now);
    const out = [];
    for (const b of buses) {
      const t = byId.get(b.id);
      if (b.atStopId !== null) atStopNow.set(b.id, b.atStopId);
      const evidenceAt = t ? (t.lastEvidenceAt ?? t.polledAt) : now;
      out.push({
        id: b.id,
        line: b.line,
        color: o.ds.linesById.get(b.line)?.color ?? "#333",
        light: LIGHT_LINES.has(b.line),
        ll: b.ll,
        bearing: b.bearing,
        atStop: b.atStopId !== null,
        num: numById.get(b.id),
        observedAt: evidenceAt,
        confidence:
          !t || (t.confidence === "medium" && now - evidenceAt <= EXTRAPOLATE_MS)
            ? ("medium" as const)
            : ("low" as const),
      });
    }
    o.adapter.setBuses(out);
    renderLabels();
  };

  // PRUEBA: minutos de AUESA encima de cada parada de las líneas activas.
  const renderLabels = (): void => {
    if (!STOP_ETA_LABELS) return;
    const now = Date.now();
    const out: StopLabelInput[] = [];
    if (enabled)
      for (const id of engine.stopsOf(active)) {
        const stop = o.ds.stopsById.get(id);
        if (!stop) continue;
        const best = new Map<LineId, number>();
        for (const e of lastEtas.get(id) ?? []) {
          if (!active.has(e.line) || now - (e.observedAt ?? 0) > LABEL_FRESH_MS) continue;
          best.set(e.line, Math.min(best.get(e.line) ?? Infinity, e.minutes));
        }
        const poll = lastPolls.get(id);
        if (!poll) continue;
        // Solo la cuenta atrás hasta la siguiente lectura; el color dice cuánto le falta al bus.
        const left = Math.ceil((poll.due - now) / 1000);
        const text = !poll.due || left <= 0 ? "leyendo" : `${left} s`;
        const min = best.size ? Math.min(...best.values()) : null;
        const level = min === null ? null : min === 0 ? "now" : min === 1 ? "soon" : "far";
        const first = [...best.keys()][0] ?? [...active][0]!;
        out.push({
          id,
          ll: stop.ll,
          text,
          level,
          color: o.ds.linesById.get(first)?.color ?? "#333",
          light: LIGHT_LINES.has(first),
        });
      }
    o.adapter.setStopLabels?.(out);
  };

  const startFrames = (): void => {
    if (frameTimer === null) frameTimer = window.setInterval(render, FRAME_MS);
  };
  const stopFrames = (): void => {
    if (frameTimer !== null) window.clearInterval(frameTimer);
    frameTimer = null;
  };

  const updated = (): void => {
    for (const cb of updateCbs) cb();
    render();
  };

  // --- Datos del servicio ------------------------------------------------------------------

  const apply = (msg: LiveMessage): void => {
    const now = Date.now();
    // Los instantes vienen en el reloj del servidor: se pasan al del móvil.
    const off = now - msg.now;
    const before = new Map(tracked.map((t) => [t.id, t]));
    const next: Tracked[] = [];
    for (const s of msg.buses) {
      if (!active.has(s.bus.line)) continue;
      const t = engine.fromState({
        ...s,
        bus: {
          ...s.bus,
          ...(s.bus.observedAt === undefined ? {} : { observedAt: s.bus.observedAt + off }),
          ...(s.bus.inferredAt === undefined ? {} : { inferredAt: s.bus.inferredAt + off }),
        },
        polledAt: s.polledAt + off,
        shownAt: s.shownAt + off,
        obsT: s.obsT + off,
        ...(s.lastEvidenceAt === undefined ? {} : { lastEvidenceAt: s.lastEvidenceAt + off }),
        blendFrom: s.blendFrom ? { ...s.blendFrom, at: s.blendFrom.at + off } : null,
      });
      if (!t) continue;
      const mine = before.get(t.id);
      if (mine) before.delete(t.id);
      // Si aquí se veía en otro sitio (el servicio ha ido por delante unos segundos, o veníamos
      // de datos propios con otros ids), se desliza desde donde se veía.
      const prev = mine ?? nearest(t);
      const to = engine.currentLL(t, now)?.ll;
      if (prev?.lastLL && to && !t.blendFrom) {
        const m = haversine(prev.lastLL, to);
        if (m > 3) t.blendFrom = { ll: prev.lastLL, at: now, ms: BLEND_MS };
      }
      t.lastLL = prev?.lastLL ?? null;
      next.push(t);
    }
    function nearest(t: Tracked): Tracked | null {
      const p = engine.currentLL(t, now)?.ll;
      if (!p) return null;
      let best: Tracked | null = null;
      let bestM = HANDOVER_M;
      for (const x of before.values()) {
        if (x.bus.line !== t.bus.line || !x.lastLL) continue;
        const m = haversine(x.lastLL, p);
        if (m < bestM) [best, bestM] = [x, m];
      }
      if (best) before.delete(best.id);
      return best;
    }
    tracked = next;
    numById = new Map(msg.buses.flatMap((s) => (s.num ? [[s.id, s.num] as [string, number]] : [])));
    lastEtas = new Map(
      Object.entries(msg.etas).map(([k, v]) => [
        Number(k),
        v.map(([line, destination, minutes, observedAt]) => ({
          line,
          destination,
          minutes,
          observedAt: (observedAt ?? msg.now) + off,
        })),
      ]),
    );
    applyPolls(msg);
    serverAt =
      msg.observedAt === undefined
        ? now
        : msg.observedAt > 0
          ? Math.min(now, msg.observedAt + off)
          : 0;
    lastPoll = now;
    polledLines = new Set(active);
    errorShown = false;
    updated();
  };

  const applyPolls = (
    msg: Pick<LiveMessage, "now" | "polls"> & { etas?: LiveMessage["etas"] },
  ): void => {
    const off = Date.now() - msg.now;
    // Lecturas nuevas desde el último aviso: el color de la parada cambia sin esperar al paso.
    for (const [k, v] of Object.entries(msg.etas ?? {}))
      lastEtas.set(
        Number(k),
        v.map(([line, destination, minutes, observedAt]) => ({
          line,
          destination,
          minutes,
          observedAt: (observedAt ?? msg.now) + off,
        })),
      );
    lastPolls = new Map(
      Object.entries(msg.polls ?? {}).map(([k, [at, due]]) => [
        Number(k),
        { at: at ? at + off : 0, due: due ? due + off : 0 },
      ]),
    );
  };

  const closeSource = (): void => {
    source?.close();
    source = null;
  };
  const openSource = (): void => {
    closeSource();
    if (!useServer || !enabled || active.size === 0 || document.hidden) return;
    const es = new EventSource(
      `/api/live/stream?lines=${encodeURIComponent([...active].join(","))}`,
    );
    es.onmessage = (ev: MessageEvent<string>) => {
      if (source !== es || !enabled) return;
      try {
        apply(JSON.parse(ev.data) as LiveMessage);
      } catch {
        // Un mensaje roto no tumba nada: llegará otro.
      }
    };
    es.addEventListener("polls", (ev: MessageEvent<string>) => {
      if (source !== es || !enabled) return;
      try {
        applyPolls(JSON.parse(ev.data) as Pick<LiveMessage, "now" | "polls">);
      } catch {
        // Llegará otro en 2 s.
      }
    });
    es.addEventListener("pos", (ev: MessageEvent<string>) => {
      if (source !== es || !enabled) return;
      try {
        const msg = JSON.parse(ev.data) as PosMessage;
        const now = Date.now();
        pos = new Map(msg.b.map((row) => [row[0], { row, at: now }]));
        for (const row of msg.b) if (row[2]) numById.set(row[0], row[2]);
        posAt = now;
        render();
      } catch {
        // Llegará otro en 1 s.
      }
    });
    // EventSource reintenta solo; mientras tanto la consulta propia cubre el hueco.
    source = es;
  };

  // --- Consulta propia (sin servicio) -------------------------------------------------------

  const pool = async <T>(
    items: T[],
    fn: (it: T) => Promise<void>,
    signal: AbortSignal,
  ): Promise<void> => {
    let next = 0;
    const worker = async (): Promise<void> => {
      while (!signal.aborted && next < items.length) await fn(items[next++]!);
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, items.length) }, worker));
  };

  const poll = async (): Promise<void> => {
    if (polling) return;
    const gen = generation;
    // Los minutos son de cuando empieza la consulta (o algo antes: nginx los cachea 15 s).
    const startedAt = Date.now();
    polling = true;
    const ctl = new AbortController();
    pollCtl = ctl;
    let watchdog: number | null = null;
    let stopCount = 0;
    try {
      if (!enabled || active.size === 0 || document.hidden || serverFresh()) return;
      const want = engine.stopsOf(active);
      stopCount = want.size;
      const etas = new Map<number, ApiEstimation[]>();
      await Promise.race([
        pool(
          [...want],
          async (id) => {
            try {
              const list = await fetchEstimations(id, ctl.signal);
              if (!ctl.signal.aborted)
                etas.set(
                  id,
                  list.map((e) => ({ ...e, observedAt: e.observedAt ?? Date.now() })),
                );
            } catch {
              // Una parada que falla no tumba la consulta: ese tramo se queda sin dato.
            }
          },
          ctl.signal,
        ),
        new Promise<never>((_resolve, reject) => {
          watchdog = window.setTimeout(() => {
            ctl.abort();
            reject(new Error("La consulta de tiempo real ha superado 60 s"));
          }, POLL_STUCK_MS);
          ctl.signal.addEventListener(
            "abort",
            () => reject(new DOMException("Aborted", "AbortError")),
            { once: true },
          );
        }),
      ]);
      if (gen !== generation || serverFresh()) return;
      if (want.size > 0 && etas.size === 0) throw new Error("La API de tiempo real no responde");

      const fresh = engine.infer(active, etas, Date.now());
      lastPoll = Date.now();
      lastEtas = etas;
      const nextPollS =
        Math.max(POLL_MIN_S, Math.min(POLL_MAX_S, want.size * POLL_S_PER_STOP)) +
        (Date.now() - startedAt) / 1000;
      tracked = engine.merge(tracked, fresh, {
        now: Date.now(),
        polledAt: Date.now(),
        nextPollS,
        polledLines,
        active,
        newId: () => `bus-${++seq}`,
      });
      polledLines = new Set(active);
      errorShown = false;
      updated();
    } catch (err) {
      if (gen !== generation) return;
      if (!errorShown) o.onError?.(err);
      errorShown = true;
    } finally {
      if (watchdog !== null) window.clearTimeout(watchdog);
      // Una consulta de una generación anterior no toca el estado de la actual.
      if (gen === generation) {
        polling = false;
        pollCtl = null;
        schedule(stopCount);
      }
    }
  };

  const schedule = (stopCount: number): void => {
    if (pollTimer !== null) window.clearTimeout(pollTimer);
    pollTimer = null;
    if (!enabled || active.size === 0) return;
    const s = Math.max(POLL_MIN_S, Math.min(POLL_MAX_S, stopCount * POLL_S_PER_STOP));
    pollTimer = window.setTimeout(() => void poll(), s * 1000);
  };

  const restart = (): void => {
    generation++;
    pollCtl?.abort();
    pollCtl = null;
    polling = false;
    if (pollTimer !== null) window.clearTimeout(pollTimer);
    pollTimer = null;
    if (!enabled || active.size === 0) {
      closeSource();
      tracked = [];
      polledLines.clear();
      stopFrames();
      o.adapter.setBuses([]);
      lastEtas = new Map();
      lastPolls = new Map();
      pos = new Map();
      posAt = 0;
      renderLabels();
      return;
    }
    startFrames();
    render();
    polling = false;
    serverAt = 0; // las líneas nuevas aún no han llegado del servicio
    if (useServer) {
      openSource();
      // Si el servicio no contesta enseguida, consultamos nosotros (y seguimos cada poco
      // comprobando: en cuanto llegue algo del servicio, la consulta propia se calla).
      pollTimer = window.setTimeout(() => void poll(), SERVER_WAIT_MS);
    } else void poll();
  };

  document.addEventListener("visibilitychange", () => {
    if (document.hidden) {
      generation++;
      pollCtl?.abort();
      pollCtl = null;
      polling = false;
      stopFrames();
      closeSource(); // en segundo plano no gastamos batería ni datos
      return;
    }
    if (!enabled || active.size === 0) return;
    startFrames();
    openSource();
    if (Date.now() - lastPoll > POLL_MIN_S * 1000) {
      if (pollTimer !== null) window.clearTimeout(pollTimer);
      pollTimer = window.setTimeout(() => void poll(), useServer ? SERVER_WAIT_MS : 0);
    }
  });

  return {
    setLines(ids: readonly LineId[]): void {
      const next = new Set(ids);
      const added = [...next].some((l) => !active.has(l));
      const removed = [...active].some((l) => !next.has(l));
      active = next;
      if (added) restart();
      else if (active.size === 0) restart();
      else {
        // La suscripción también determina qué paradas sondea el servidor.
        if (removed) openSource();
        render();
        renderLabels();
      }
    },
    setEnabled(on: boolean): void {
      if (on === enabled) return;
      enabled = on;
      restart();
    },
    lineOf(id: string): LineId | null {
      return tracked.find((x) => x.id === id)?.bus.line ?? null;
    },
    nextAt(stopId: number, line: LineId, routeId: number): number | null {
      return engine.nextAt(lastEtas, stopId, line, routeId);
    },
    busesOn(
      line: LineId,
      routeId: number,
    ): { fromStopId: number; toStopId: number; atStopId: number | null }[] {
      const entry = engine.entryOf(`${line}|${routeId}`);
      if (!entry) return [];
      // Un bus detectado en otra variante del mismo sentido también circula por aquí si su
      // tramo forma parte de este trayecto.
      const ids = entry.route.stops.map((s) => s.id);
      return tracked
        .filter((t) => t.shown && t.bus.line === line && t.bus.sense === entry.route.sense)
        .filter((t) => {
          const i = ids.indexOf(t.bus.fromStopId);
          return i >= 0 && ids[i + 1] === t.bus.toStopId;
        })
        .filter((t) => Date.now() - (t.lastEvidenceAt ?? t.polledAt) <= EVIDENCE_STALE_MS)
        .map((t) => ({
          fromStopId: t.bus.fromStopId,
          toStopId: t.bus.toStopId,
          atStopId: atStopNow.get(t.id) ?? null,
        }));
    },
    atStop(_stopId: number, _line: LineId): boolean {
      // Las estimaciones por minutos no certifican la presencia física en una parada.
      return false;
    },
    onUpdate(cb: () => void): () => void {
      updateCbs.push(cb);
      return () => void updateCbs.splice(updateCbs.indexOf(cb) >>> 0, 1);
    },
    describe(id: string): string | null {
      const t = tracked.find((x) => x.id === id);
      if (!t) return null;
      const next = nextStopAt(t.bus.timeline, (Date.now() - t.polledAt) / 1000);
      const num = numById.get(id);
      const head = `${t.bus.line}${num ? ` nº ${num}` : ""} → ${titleCase(t.bus.destination)}`;
      const age = Math.max(0, Math.round((Date.now() - (t.lastEvidenceAt ?? t.polledAt)) / 1000));
      const confidence =
        t.confidence === "medium" && age * 1000 <= EXTRAPOLATE_MS ? "media" : "baja";
      const evidence = `posición estimada · confianza ${confidence} · datos de hace ${age} s`;
      const here = atStopNow.get(t.id);
      if (here !== undefined) {
        const name = o.ds.stopsById.get(here)?.name ?? "";
        return `${head} · llegando a ${titleCase(name)} · ${evidence}`;
      }
      if (!next) return `${head} · ${evidence}`;
      const stop = t.entry.route.stops[next.stopIdx];
      const min = Math.round(next.seconds / 60);
      const when = min <= 0 ? "llegando" : `en ${min} min`;
      return `${head} · ${titleCase(stop?.name ?? "")} ${when} · ${evidence}`;
    },
  };
}

function titleCase(s: string): string {
  return s
    .toLowerCase()
    .replace(/(^|[\s.\-/(])(\p{L})/gu, (_m, p: string, c: string) => p + c.toUpperCase());
}
